import * as THREE from "three";
import { U } from "./streetScene.js";
import { kaponHeightAt } from "./kaponTerrain.js";
import type { Chest } from "@ghostland/shared";

/**
 * Renders loot CHESTS dropped by slain mobs: an open treasure chest billboard with a soft, pulsing
 * shaft of light beaming up out of it (additive glow). Static — position comes from the synced
 * `Chest` schema; the controller just spawns/removes sprites and animates the glow.
 */

/** Draw a small open treasure chest (gold spilling out) onto a 32x28 canvas. */
function makeChestTexture(): THREE.CanvasTexture {
  const W = 32, H = 28;
  const c = document.createElement("canvas"); c.width = W; c.height = H;
  const ctx = c.getContext("2d")!; ctx.imageSmoothingEnabled = false;
  const r = (x: number, y: number, w: number, h: number, col: string) => { ctx.fillStyle = col; ctx.fillRect(x, y, w, h); };
  const WOOD = "#7a4a22", WOOD_D = "#5a3416", BAND = "#caa23c", BAND_D = "#8a6a20", GOLD = "#ffd23f";
  // open lid (tilted up, behind)
  r(5, 2, 22, 7, WOOD_D); r(6, 3, 20, 4, WOOD); r(5, 2, 22, 2, BAND_D);
  // chest base (front box)
  r(4, 12, 24, 14, WOOD_D); r(5, 13, 22, 12, WOOD);
  r(4, 12, 24, 2, BAND); r(4, 18, 24, 2, BAND_D); // metal bands
  r(14, 19, 4, 5, BAND); r(15, 20, 2, 3, BAND_D); // lock
  // gold spilling at the open mouth
  r(6, 9, 20, 4, GOLD); r(7, 8, 5, 2, GOLD); r(15, 7, 4, 2, GOLD); r(20, 9, 4, 2, GOLD);
  r(8, 9, 2, 1, "#fff7c4"); r(17, 8, 2, 1, "#fff7c4");
  const t = new THREE.CanvasTexture(c);
  t.magFilter = t.minFilter = THREE.NearestFilter; t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

/** Soft radial glow (white→transparent) for the additive shine. */
function makeGlowTexture(): THREE.CanvasTexture {
  const S = 64; const c = document.createElement("canvas"); c.width = c.height = S;
  const ctx = c.getContext("2d")!;
  const g = ctx.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 2);
  g.addColorStop(0, "rgba(255,244,190,0.95)");
  g.addColorStop(0.4, "rgba(255,214,80,0.45)");
  g.addColorStop(1, "rgba(255,210,60,0)");
  ctx.fillStyle = g; ctx.fillRect(0, 0, S, S);
  const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

interface ChestEntity { x: number; z: number; group: THREE.Group; glow: THREE.Sprite; beam: THREE.Sprite; }

export class ChestController {
  private readonly chests = new Map<string, ChestEntity>();
  private readonly chestTex = makeChestTexture();
  private readonly glowTex = makeGlowTexture();
  private visible = true;
  private t = 0;

  constructor(private readonly scene: THREE.Scene) {}

  add(key: string, chest: Chest): void {
    if (this.chests.has(key)) return;
    const group = new THREE.Group();
    group.position.set(chest.x * U, 0.02 + kaponHeightAt(chest.x * U, chest.y * U), chest.y * U);
    group.visible = this.visible;

    const body = new THREE.Sprite(new THREE.SpriteMaterial({ map: this.chestTex, transparent: true, alphaTest: 0.5 }));
    body.center.set(0.5, 0); body.scale.set(0.95, 0.83, 1);
    group.add(body);

    // A tall, soft shaft of light rising out of the open chest.
    const beam = new THREE.Sprite(new THREE.SpriteMaterial({ map: this.glowTex, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false }));
    beam.center.set(0.5, 0); beam.scale.set(0.6, 1.8, 1); beam.position.set(0, 0.25, 0);
    group.add(beam);

    // A round glow pooling at the chest mouth.
    const glow = new THREE.Sprite(new THREE.SpriteMaterial({ map: this.glowTex, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false }));
    glow.center.set(0.5, 0.5); glow.scale.set(1.1, 1.1, 1); glow.position.set(0, 0.45, 0);
    group.add(glow);

    this.scene.add(group);
    this.chests.set(key, { x: chest.x * U, z: chest.y * U, group, glow, beam });
  }

  remove(key: string): void {
    const e = this.chests.get(key); if (!e) return;
    this.scene.remove(e.group);
    e.group.traverse((o) => { if (o instanceof THREE.Sprite) (o.material as THREE.SpriteMaterial).dispose(); });
    this.chests.delete(key);
  }

  setVisible(v: boolean): void {
    if (v === this.visible) return;
    this.visible = v;
    for (const e of this.chests.values()) e.group.visible = v;
  }

  /** Nearest chest within `range` server px of (px,py), or null. Used for the open prompt + E action. */
  nearest(px: number, py: number, range: number): { key: string; x: number; y: number } | null {
    let best = range * range, found: { key: string; x: number; y: number } | null = null;
    for (const [key, e] of this.chests) {
      const dx = e.x / U - px, dy = e.z / U - py, d2 = dx * dx + dy * dy;
      if (d2 <= best) { best = d2; found = { key, x: e.x / U, y: e.z / U }; }
    }
    return found;
  }

  tick(dt: number, visible: boolean): void {
    this.setVisible(visible);
    if (!visible) return;
    this.t += dt;
    const pulse = 0.85 + Math.sin(this.t * 3) * 0.15;
    for (const e of this.chests.values()) {
      (e.glow.material as THREE.SpriteMaterial).opacity = pulse;
      e.glow.scale.set(1.0 + pulse * 0.25, 1.0 + pulse * 0.25, 1);
      (e.beam.material as THREE.SpriteMaterial).opacity = 0.4 + pulse * 0.3;
    }
  }

  dispose(): void {
    for (const key of [...this.chests.keys()]) this.remove(key);
    this.chestTex.dispose(); this.glowTex.dispose();
  }
}
